import without from 'lodash/without';

import {
  PoolStatus,
  TelegramCallsTableSortKeys,
} from 'api/types/httpsTypes/contracts';
import { isNotNil } from 'modules/utils/isNotNil';
import { allSuffixes, formatNumberWithSuffix } from 'utils/FormatNumber';
import { LiquidityRangeFilter, MarketcapRangeFilter } from './ResearchFilters';
import { NewCallsFilterState, useCallsSearchFilters } from './usePageParams';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../../components/dropdown/sort-dropdown';
import {
  ActivteFiltersBar,
  FilterTag,
  SelectFiltersBar,
} from '../../../components/filters';
import { twMerge } from 'tailwind-merge';
import { FastBuy } from '../../../terminal/components/FastBuy';
import { useWallets } from 'modules/routing/walletProvider';

const STATUS_OPTIONS = [
  {
    value: 'SNIPEABLE' as PoolStatus,
    label: 'Not Launched',
    icon: null,
  },
  {
    value: 'NOT_SNIPEABLE' as PoolStatus,
    label: 'Launched',
    icon: null,
  },
  {
    value: 'RUG_PULL' as PoolStatus,
    label: 'Rug-pull',
    icon: null,
  },
];

export const SORT_OPTIONS = [
  {
    label: 'Last Call',
    value: `-${TelegramCallsTableSortKeys.LastCall}`,
  },
  {
    label: 'Number of Calls',
    value: `-${TelegramCallsTableSortKeys.NumberOfCalls}`,
  },
  {
    label: 'Liquidity',
    value: `-${TelegramCallsTableSortKeys.Liquidity}`,
  },
  {
    label: 'Fully Diluted Market Cap',
    value: `-${TelegramCallsTableSortKeys.Marketcap}`,
  },
  {
    label: 'Newest Pool',
    value: `-${TelegramCallsTableSortKeys.CreatedAt}`,
  },
];

export const CallsFiltersBar = () => {
  const { filtersState: filters, setFilters } = useCallsSearchFilters();
  const { data: wallets } = useWallets();
  const nOfFiltersActive = [
    filters.channelId,
    filters.minLiquidity,
    filters.maxLiquidity,
    filters.minMarketcap,
    filters.maxMarketcap,
    filters.status,
  ].filter((value) => value !== undefined && value).length;

  const updateFilters = (update: Partial<NewCallsFilterState>) => {
    setFilters({ ...filters, offset: 0, ...update });
  };
  const isLargeScreen = useMediaQuery(screenGte.large);
  return (
    <div className="xxs:mx-1 lg:mx-0 space-y-1">
      <div className="flex justify-between items-center">
        <SelectFiltersBar
          icon={
            <div
              className={twMerge(
                'xxs:p-1 flex xxs:space-x-1 items-center',
                nOfFiltersActive
                  ? 'xxs:hover:text-dex-white'
                  : ' xxs:hover:text-dex-white xxs:text-dex-white-secondary'
              )}
            >
              <FunnelIcon aria-hidden="true" width={16} height={16} />
              {nOfFiltersActive ? <div>{nOfFiltersActive}</div> : ''}
            </div>
          }
          sort={
            !isLargeScreen && (
              <div className="lg:hidden gap-x-1">
                <CallsSort
                  value={filters.sort}
                  onChange={(value) => {
                    updateFilters({
                      sort: value,
                    });
                  }}
                />
              </div>
            )
          }
        >
          <SortDropdown
            className="xxs:bg-dex-black-800"
            value={
              filters?.status?.[0]
                ? (filters.status[0] as PoolStatus)
                : (undefined as unknown as any)
            }
            onChange={(value: PoolStatus) => {
              if (value === filters.status?.[0]) {
                updateFilters({
                  status: undefined,
                });
              } else {
                updateFilters({
                  status: [value],
                });
              }
            }}
            options={STATUS_OPTIONS}
            mainLabel="Status"
          />

          <LiquidityRangeFilter />
          <MarketcapRangeFilter />
        </SelectFiltersBar>
        {!!wallets?.length && (
          <div className="hidden lg:block xxs:mr-2 lg:mr-0">
            <FastBuy />
          </div>
        )}
      </div>

      <ActivteFiltersBar>
        {filters.status?.map((status) => (
          <FilterTag
            key={status}
            onRemove={() => {
              return updateFilters({
                status: without(filters.status, status),
              });
            }}
            label={
              STATUS_OPTIONS.find((el) => el.value === status)?.label || ''
            }
          />
        ))}

        {isNotNil(filters.minLiquidity) ? (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minLiquidity: undefined,
              })
            }
            label={`Liquidity > $${formatNumberWithSuffix(
              filters.minLiquidity,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}`}
          />
        ) : null}
        {isNotNil(filters.maxLiquidity) ? (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxLiquidity: undefined,
              })
            }
            label={`Liquidity < $${formatNumberWithSuffix(
              filters.maxLiquidity,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}`}
          />
        ) : null}
        {isNotNil(filters.minMarketcap) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minMarketcap: undefined,
              })
            }
            label={`Marketcap > $${formatNumberWithSuffix(
              filters.minMarketcap,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}`}
          />
        )}
        {isNotNil(filters.maxMarketcap) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxMarketcap: undefined,
              })
            }
            label={`Marketcap < $${formatNumberWithSuffix(
              filters.maxMarketcap,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}`}
          />
        )}
      </ActivteFiltersBar>
    </div>
  );
};

export function CallsSort(props: Omit<SortDropdownProps<string>, 'options'>) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:py-2 lg:py-1.5 xxs:px-2 h-auto"
      {...props}
      options={SORT_OPTIONS}
    />
  );
}
